import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { styled } from "@mui/material/styles";
import BryxInput from "../BryxInput";
import BryxBox from "../BryxBox";
import { IconButton } from "@mui/material";
import BryxIcon from "../BryxIcon/BryxIcon";
import BryxPasswordStrengthChip from "./BryxPasswordStrengthChip";
import { useEffect, useState } from "react";
import BryxTypography from "../BryxTypography";
import BryxColors from "../../types/theme";
export var BryxPasswordInputCharacterClass;
(function (BryxPasswordInputCharacterClass) {
    BryxPasswordInputCharacterClass[BryxPasswordInputCharacterClass["number"] = 0] = "number";
    BryxPasswordInputCharacterClass[BryxPasswordInputCharacterClass["symbol"] = 1] = "symbol";
    BryxPasswordInputCharacterClass[BryxPasswordInputCharacterClass["uppercaseLetter"] = 2] = "uppercaseLetter";
    BryxPasswordInputCharacterClass[BryxPasswordInputCharacterClass["lowercaseLetter"] = 3] = "lowercaseLetter";
    BryxPasswordInputCharacterClass[BryxPasswordInputCharacterClass["space"] = 4] = "space";
})(BryxPasswordInputCharacterClass || (BryxPasswordInputCharacterClass = {}));
const DEFAULT_DISPLAY_TEXT = {
    characterClasses: {
        [BryxPasswordInputCharacterClass.number]: "Number",
        [BryxPasswordInputCharacterClass.symbol]: "Symbol",
        [BryxPasswordInputCharacterClass.uppercaseLetter]: "Uppercase Letter",
        [BryxPasswordInputCharacterClass.lowercaseLetter]: "Lowercase Letter",
        [BryxPasswordInputCharacterClass.space]: "Space",
    },
    newPrompt: "New Password",
    confirmPrompt: "Confirm Password",
};
const CHARACTER_CLASS_MATCHERS = {
    [BryxPasswordInputCharacterClass.number]: /\d/g,
    [BryxPasswordInputCharacterClass.symbol]: /[~!@#$%^&*()_+\-=\[\]\\{}|;':",.\/<>?]/g,
    [BryxPasswordInputCharacterClass.uppercaseLetter]: /[A-Z]/g,
    [BryxPasswordInputCharacterClass.lowercaseLetter]: /[a-z]/g,
    [BryxPasswordInputCharacterClass.space]: /\s/g,
};
const BryxPasswordInput = styled((props) => {
    const { mode, sx, innerWidth, innerHeight } = props;
    const value = props.value;
    const [passedValidationCriteriaCount, setPassedValidationCriteriaCount] = useState(0);
    const [passedValidationCriteria, setPassedValidationCriteria] = useState([]);
    const [textVisible, setTextVisible] = useState(false);
    const validate = () => {
        if (value == null || value == "") {
            setPassedValidationCriteriaCount(0);
            setPassedValidationCriteria([]);
            return;
        }
        const { requiredValidationCriteriaCount, textValidationCriteria } = props;
        let newPassedCriteriaCount = 0;
        let passedClasses = [];
        if (requiredValidationCriteriaCount && textValidationCriteria) {
            textValidationCriteria.map((criteria) => {
                if (value.match(CHARACTER_CLASS_MATCHERS[criteria])) {
                    passedClasses.push(criteria);
                    newPassedCriteriaCount++;
                }
            });
            setPassedValidationCriteriaCount(newPassedCriteriaCount);
            setPassedValidationCriteria(passedClasses);
        }
    };
    const hookedOnChange = (e) => {
        if (mode == "new") {
            validate();
        }
        props.onChange && props.onChange(e);
    };
    useEffect(() => {
        if (mode == "new") {
            validate();
        }
    }, [value]);
    // To be aesthetically pleasing, all incomplete stages should be last, so even if the character classes are satisfied,
    // if the length still isn't, that chip should not be statically shown first as incomplete, the last chip should
    // be incomplete
    const allCharacterClassesSatisfied = passedValidationCriteriaCount >= (props.requiredValidationCriteriaCount ?? 0);
    const lengthRequirementSatisfied = props.requiredLength != null ? (value?.length ?? 0) >= props.requiredLength : true;
    const allComplete = allCharacterClassesSatisfied && lengthRequirementSatisfied;
    const validationChips = () => {
        const chips = [];
        for (let i = 0; i < (props.requiredValidationCriteriaCount ?? 0); i++) {
            if (allComplete) {
                chips.push(_jsx(BryxPasswordStrengthChip, { className: "allComplete" }));
            }
            else {
                chips.push(_jsx(BryxPasswordStrengthChip, { className: `${i < passedValidationCriteriaCount ? "stageComplete" : "incomplete"}` }));
            }
        }
        return chips;
    };
    const shouldShowInstructionPrompt = mode == "new" && (props.requiredLength != null || props.requiredValidationCriteriaCount != null);
    const hasLengthRequirement = mode == "new" && props.requiredLength != null;
    const hasContentRequirement = mode == "new" && props.requiredValidationCriteriaCount != null && props.requiredValidationCriteriaCount > 0;
    const instructionPrompt = () => {
        if (!shouldShowInstructionPrompt) {
            return null;
        }
        const lengthText = hasLengthRequirement ? `${props.requiredLength} Characters` : "";
        const joiner = hasLengthRequirement && hasContentRequirement ? " and " : "";
        const classText = hasContentRequirement ? `${props.requiredValidationCriteriaCount} of the Following:` : "";
        return `Must Contain ${lengthText}${joiner}${classText}`;
    };
    return (_jsxs(BryxBox, { isFlex: true, sx: { ...sx, gap: "4px", flexDirection: "column", rowGap: "16px" }, className: props.className, children: [_jsx(BryxInput, { value: props.value ?? "", fullWidth: true, label: mode == "new"
                    ? props.displayText?.newPrompt ?? DEFAULT_DISPLAY_TEXT.newPrompt
                    : props.displayText?.confirmPrompt ?? DEFAULT_DISPLAY_TEXT.confirmPrompt, InputProps: {
                    endAdornment: (_jsx(IconButton, { onClick: () => setTextVisible(!textVisible), children: _jsx(BryxIcon, { iconName: "visibility", lgIconSize: true, selected: textVisible }) })),
                }, onChange: hookedOnChange, type: textVisible ? "text" : "password", error: mode != "new" ? props.error : undefined, errorText: mode != "new" ? props.errorText : undefined, innerHeight: innerHeight, innerWidth: innerWidth }), mode == "new" && (_jsx(BryxBox, { isFlex: true, fullWidth: true, sx: { gap: "4px", flexDirection: "row", marginTop: "-12px" }, children: allComplete ? (_jsxs(_Fragment, { children: [_jsx(BryxPasswordStrengthChip, { className: "allComplete" }), ...validationChips()] })) : (_jsxs(_Fragment, { children: [lengthRequirementSatisfied && _jsx(BryxPasswordStrengthChip, { className: "stageComplete" }), ...validationChips(), !lengthRequirementSatisfied && _jsx(BryxPasswordStrengthChip, { className: "incomplete" })] })) })), mode == "new" && (_jsx(BryxBox, { isFlex: true, fullWidth: true, children: shouldShowInstructionPrompt && (_jsx(BryxTypography, { variant: "body0", className: "passwordHintText", children: instructionPrompt() })) })), mode == "new" && (_jsx(BryxBox, { isFlex: true, fullWidth: true, children: hasContentRequirement && (_jsx(BryxBox, { isFlex: true, fullWidth: true, smGap: true, sx: { whiteSpace: "nowrap", flexFlow: "wrap" }, children: props.textValidationCriteria?.map((criteria) => (_jsx(BryxBox, { children: passedValidationCriteria.includes(criteria) ? (_jsxs(BryxTypography, { variant: "body1", className: "passwordCriteriaComplete", children: [_jsx(BryxIcon, { iconName: "check", smIconSize: true }), " ", (props.displayText || DEFAULT_DISPLAY_TEXT).characterClasses[criteria]] })) : (_jsxs(BryxTypography, { variant: "body1", className: "passwordCriteriaIncomplete", children: ["\u2022 ", (props.displayText || DEFAULT_DISPLAY_TEXT).characterClasses[criteria]] })) }, `criteriaChip-${criteria}`))) ?? undefined })) }))] }));
})(({ theme }) => {
    const palette = theme.palette.mode;
    return {
        "& .MuiInputBase-root": {
            height: "42px",
            "& .MuiIconButton-root:hover": {
                backgroundColor: "unset !important",
                color: BryxColors.brand2(palette),
            },
            "&:hover .MuiIconButton-root": {
                backgroundColor: "unset !important",
                color: BryxColors.brand2(palette),
            },
        },
        "& .passwordHintText": {
            fontSize: "13px",
            lineHeight: "100%",
            fontWeight: "600",
            color: BryxColors.brandPrimary(palette),
        },
        "& .passwordCriteriaComplete": {
            color: BryxColors.notify(palette, "inactive"),
            "& svg": {
                height: "13px",
                width: "13px",
                transform: "translateY(2px)",
                fill: BryxColors.notify(palette, "inactive"),
            },
        },
    };
});
export default BryxPasswordInput;
